import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Shield,
  Lock,
  Eye,
  FileText,
  Mail,
  MapPin,
  Bell,
  Settings,
  LogOut,
  Moon,
  Sun,
  Image as ImageIcon
} from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';

export default function WorkdayClientPrivacy() {
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const navigate = useNavigate();

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [notifications] = useState(3);

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setLogoLoading(false);
          return;
        }

        if (data?.value) {
          const img = new Image();
          img.onload = () => {
            setLogoUrl(data.value);
            setLogoLoading(false);
          };
          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setLogoLoading(false);
          };
          img.src = data.value;
        } else {
          setLogoLoading(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setLogoLoading(false);
      }
    } catch (err) {
      setLogoLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/customer/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-gray-900 text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      <WorkdayClientHeader />

      <div className="max-w-5xl mx-auto px-6 pt-32 pb-12">
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/workday-client/settings')}
            className={`mr-4 p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <Shield className="w-8 h-8 mr-3 text-[#40E0D0]" />
              Privacy Statement
            </h1>
          </div>
        </div>

        <div className={`${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } border rounded-xl p-8 space-y-8`}>
          <div className="prose max-w-none">
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              We take your privacy seriously and are committed to protecting your personal information. This Privacy Policy explains how Perplexity collects, uses, and protects your information when you use our services.
            </p>
          </div>

          <section>
            <h2 className={`text-2xl font-bold mb-4 flex items-center ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <Eye className="w-6 h-6 mr-2 text-[#40E0D0]" />
              1. Information We May Collect
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className={`text-xl font-semibold mb-3 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  1.1 Personal Information
                </h3>
                <ul className={`list-disc list-inside space-y-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li>Name and contact information</li>
                  <li>Account credentials</li>
                  <li>Billing information</li>
                  <li>Company information</li>
                  <li>Video conferencing preferences and settings</li>
                  <li>Profile pictures and display names</li>
                </ul>
              </div>

              <div>
                <h3 className={`text-xl font-semibold mb-3 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  1.2 Usage Data
                </h3>
                <ul className={`list-disc list-inside space-y-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <li>Log data and analytics</li>
                  <li>Feature usage statistics</li>
                  <li>Performance metrics</li>
                  <li>Video conferencing session metadata (duration, participant count)</li>
                  <li>Data analytics usage patterns</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className={`text-2xl font-bold mb-4 flex items-center ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <FileText className="w-6 h-6 mr-2 text-[#40E0D0]" />
              2. Content Data
            </h2>
            <ul className={`list-disc list-inside space-y-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <li>Business intelligence and analytics data</li>
              <li>Chat messages during video conferences</li>
              <li>Shared files and documents</li>
              <li>Meeting recordings (when enabled)</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              3. How We Use Your Information
            </h2>
            <ul className={`list-disc list-inside space-y-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <li>To provide and maintain our Services (Reports Detective and Perplexity Meet)</li>
              <li>To process your transactions</li>
              <li>To improve our Services</li>
              <li>To communicate with you</li>
              <li>To prevent fraud and abuse</li>
              <li>To facilitate video conferencing connections</li>
              <li>To generate analytics and reports</li>
              <li>To provide technical support</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-bold mb-4 flex items-center ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <Lock className="w-6 h-6 mr-2 text-[#40E0D0]" />
              4. Data Protection
            </h2>
            <p className={`mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className={`list-disc list-inside space-y-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <li>End-to-end encryption for video conferences</li>
              <li>Encryption in transit and at rest for analytics data</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
              <li>Secure cloud data centers</li>
              <li>Meeting access controls and waiting rooms</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              5. Data Sharing
            </h2>
            <p className={`mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              We do not sell your personal information. We may share data with:
            </p>
            <ul className={`list-disc list-inside space-y-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <li>Service providers and partners</li>
              <li>Legal authorities when required</li>
              <li>Third parties with your consent</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              6. Your Rights
            </h2>
            <p className={`mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              You have the right to:
            </p>
            <ul className={`list-disc list-inside space-y-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request data deletion</li>
              <li>Object to data processing</li>
              <li>Export your data</li>
              <li>Control your video conferencing settings</li>
              <li>Manage recording preferences</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-bold mb-4 flex items-center ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <Mail className="w-6 h-6 mr-2 text-[#40E0D0]" />
              7. Contact Information
            </h2>
            <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              For privacy-related inquiries, contact us at:
            </p>
            <div className={`p-6 rounded-lg ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
            }`}>
              <div className="space-y-3">
                <p className="font-semibold text-lg">Perplexity</p>
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mr-2 text-[#40E0D0] flex-shrink-0 mt-1" />
                  <div>
                    <p>5851 Legacy Cir</p>
                    <p>Plano, Texas 75024</p>
                    <p>United States</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-[#40E0D0]" />
                  <a
                    href="mailto:info@perplexityonline.com"
                    className="text-[#40E0D0] hover:underline"
                  >
                    info@perplexityonline.com
                  </a>
                </div>
              </div>
            </div>
          </section>

          <div className={`pt-6 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              This Privacy Policy was created in March 2021 and last updated in February 2025. We may update this policy periodically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
